import { useState, useEffect } from "react";
import { BellSimple, Plus, X, Trash, FilePdf, Image, ArrowSquareOut, ArrowSquareIn, Link, PencilSimple } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_OPTIONS = [
  { value: "report",        label: "Report",        desc: "Links to an uploaded PDF report" },
  { value: "internal_link", label: "Internal Link", desc: "Opens a page within this website (same tab)" },
  { value: "external_link", label: "External Link", desc: "Opens an external URL in a new tab" },
];

function inferPopupType(popup) {
  if (TYPE_OPTIONS.some((opt) => opt.value === popup?.popup_type)) {
    return popup.popup_type;
  }
  if (popup?.link_url) {
    return /^https?:\/\//i.test(popup.link_url) ? "external_link" : "internal_link";
  }
  return "report";
}

function TypeBadge({ type }) {
  if (type === "internal_link") return (
    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: "rgba(26,92,55,0.1)", color: "#1A5C37" }}>
      <ArrowSquareIn size={11} weight="bold" /> Internal
    </span>
  );
  if (type === "external_link") return (
    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: "rgba(184,92,56,0.1)", color: "#B85C38" }}>
      <ArrowSquareOut size={11} weight="bold" /> External
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: "rgba(74,84,80,0.1)", color: "#4A5450" }}>
      <FilePdf size={11} weight="bold" /> Report
    </span>
  );
}

const BLANK_FORM = { title: "", description: "", popup_type: "report", report_id: "", link_url: "", button_text: "" };

export default function AdminPopups() {
  const [popups, setPopups] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState(null); // null = create, string = edit
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [form, setForm] = useState(BLANK_FORM);
  const [error, setError] = useState("");

  const inputCls = "w-full px-3.5 py-2.5 rounded-lg border text-sm outline-none transition-colors";
  const inputStyle = { borderColor: "rgba(26,92,55,0.2)", backgroundColor: "#F7F5F0", color: "#1C211F" };
  const labelCls = "block text-xs font-medium mb-1.5 uppercase tracking-wider";

  const fetchPopups = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/popups`);
      const data = await res.json();
      setPopups(Array.isArray(data) ? data : []);
    } catch {
      setPopups([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchReports = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/reports`);
      const data = await res.json();
      setReports(Array.isArray(data) ? data : []);
    } catch {
      setReports([]);
    }
  };

  useEffect(() => {
    fetchPopups();
    fetchReports();
  }, []);

  const openCreate = () => {
    setForm(BLANK_FORM);
    setEditingId(null);
    setError("");
    setDrawerOpen(true);
  };

  const openEdit = (popup) => {
    const popupType = inferPopupType(popup);
    setForm({
      title: popup.title || "",
      description: popup.description || "",
      popup_type: popupType,
      report_id: popupType === "report" ? (popup.report_id || "") : "",
      link_url: popupType === "internal_link" || popupType === "external_link" ? (popup.link_url || "") : "",
      button_text: popup.button_text || "",
    });
    setEditingId(popup.id);
    setError("");
    setDrawerOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!form.title.trim()) { setError("Title is required."); return; }
    if (!form.description.trim()) { setError("Description is required."); return; }
    if ((form.popup_type === "internal_link" || form.popup_type === "external_link") && !form.link_url.trim()) {
      setError("URL is required for this pop-up type.");
      return;
    }

    setSubmitting(true);
    try {
      // Send only the fields that belong to the selected pop-up type.
      const body = {
        title: form.title.trim(),
        description: form.description.trim(),
        popup_type: form.popup_type,
        report_id: form.popup_type === "report" ? (form.report_id || null) : null,
        link_url: (form.popup_type === "internal_link" || form.popup_type === "external_link") ? form.link_url.trim() : null,
        button_text: (form.button_text || "").trim() || null,
      };

      const url = editingId ? `${BACKEND_URL}/api/popups/${editingId}/edit` : `${BACKEND_URL}/api/popups`;
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setError(err.detail || (editingId ? "Pop-up not found or could not be updated." : "Something went wrong."));
        return;
      }
      await fetchPopups();
      setDrawerOpen(false);
    } catch {
      setError(editingId ? "Pop-up not found or could not be updated." : "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await fetch(`${BACKEND_URL}/api/popups/${id}`, { method: "DELETE" });
      setPopups((prev) => prev.filter((p) => p.id !== id));
    } finally {
      setDeletingId(null);
    }
  };

  const selectedReport = reports.find((r) => r.id === form.report_id);
  const isEditing = !!editingId;

  return (
    <div className="relative">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h3 className="font-heading text-xl font-medium" style={{ color: "#1C211F" }}>Pop-ups</h3>
          <p className="text-sm mt-0.5" style={{ color: "#4A5450" }}>
            {popups.length} pop-up{popups.length !== 1 ? "s" : ""} configured &mdash; all are shown on the homepage
          </p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors"
          style={{ backgroundColor: "#1A5C37", color: "#F7F5F0" }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#14482B")}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#1A5C37")}
          data-testid="add-popup-btn"
        >
          <Plus size={16} weight="bold" />
          Add Pop-up
        </button>
      </div>

      {/* Info notice */}
      <div
        className="flex items-start gap-3 px-4 py-3 rounded-lg border mb-6 text-sm"
        style={{ backgroundColor: "rgba(26,92,55,0.04)", borderColor: "rgba(26,92,55,0.15)", color: "#4A5450" }}
      >
        <BellSimple size={16} weight="duotone" className="flex-shrink-0 mt-0.5" style={{ color: "#1A5C37" }} />
        <span>All pop-ups are shown on the homepage. Visitors can navigate between them using the dot indicators. Delete all pop-ups to stop showing them.</span>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-6 h-6 border-2 rounded-full animate-spin" style={{ borderColor: "#1A5C37", borderTopColor: "transparent" }} />
        </div>
      ) : popups.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: "rgba(26,92,55,0.08)" }}>
            <BellSimple size={28} weight="duotone" style={{ color: "#1A5C37" }} />
          </div>
          <p className="text-sm font-medium" style={{ color: "#1C211F" }}>No pop-ups yet</p>
          <p className="text-xs mt-1" style={{ color: "#4A5450" }}>Click &quot;Add Pop-up&quot; to create one for the homepage.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {popups.map((p, idx) => (
            <div
              key={p.id}
              className="flex items-start gap-4 p-5 rounded-xl border"
              style={{
                backgroundColor: idx === 0 ? "#fff" : "rgba(247,245,240,0.7)",
                borderColor: idx === 0 ? "rgba(26,92,55,0.25)" : "rgba(26,92,55,0.08)",
              }}
              data-testid={`popup-item-${p.id}`}
            >
              {/* Active badge */}
              {idx === 0 && (
                <div className="flex-shrink-0 mt-0.5">
                  <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: "rgba(26,92,55,0.12)", color: "#1A5C37" }}>
                    Active
                  </span>
                </div>
              )}

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <p className="text-sm font-medium" style={{ color: "#1C211F" }}>{p.title}</p>
                  <TypeBadge type={p.popup_type || "report"} />
                </div>
                <p className="text-xs mt-1 line-clamp-2" style={{ color: "#4A5450" }}>{p.description}</p>

                {p.report_title && (
                  <div className="flex items-center gap-1.5 mt-2">
                    <FilePdf size={13} weight="duotone" style={{ color: "#B85C38" }} />
                    <span className="text-xs font-medium truncate" style={{ color: "#B85C38" }}>{p.report_title}</span>
                  </div>
                )}

                {p.link_url && (
                  <div className="flex items-center gap-1.5 mt-2">
                    <Link size={13} weight="duotone" style={{ color: "#4A5450" }} />
                    <span className="text-xs truncate" style={{ color: "#4A5450" }}>{p.link_url}</span>
                  </div>
                )}

                {p.button_text && (
                  <p className="text-xs mt-1" style={{ color: "#7A8A84" }}>Button: &ldquo;{p.button_text}&rdquo;</p>
                )}
              </div>

              {/* Preview link */}
              {p.report_file_url && (
                <a
                  href={`${BACKEND_URL}${p.report_file_url}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs font-medium flex-shrink-0 px-3 py-1.5 rounded-lg border transition-colors"
                  style={{ color: "#1A5C37", borderColor: "rgba(26,92,55,0.2)" }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(26,92,55,0.06)")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                >
                  Preview
                </a>
              )}

              {/* Edit */}
              <button
                onClick={() => openEdit(p)}
                className="flex-shrink-0 p-1.5 rounded-lg transition-colors"
                style={{ color: "#1A5C37" }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(26,92,55,0.08)")}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                data-testid={`edit-popup-${p.id}`}
                title="Edit"
              >
                <PencilSimple size={16} weight="duotone" />
              </button>

              {/* Delete */}
              <button
                onClick={() => handleDelete(p.id)}
                disabled={deletingId === p.id}
                className="flex-shrink-0 p-1.5 rounded-lg transition-colors"
                style={{ color: "#B85C38" }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(184,92,56,0.08)")}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                data-testid={`delete-popup-${p.id}`}
                title="Delete"
              >
                <Trash size={16} weight="duotone" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Drawer Overlay */}
      {drawerOpen && (
        <div
          className="fixed inset-0 z-40"
          style={{ backgroundColor: "rgba(0,0,0,0.35)" }}
          onClick={() => setDrawerOpen(false)}
        />
      )}

      {/* Drawer Panel */}
      <div
        className={`fixed top-0 right-0 h-full z-50 flex flex-col transition-transform duration-300 ${
          drawerOpen ? "translate-x-0" : "translate-x-full"
        }`}
        style={{ width: "min(480px, 100vw)", backgroundColor: "#fff", boxShadow: "-8px 0 32px rgba(0,0,0,0.12)" }}
      >
        {/* Drawer Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0" style={{ borderColor: "rgba(26,92,55,0.1)" }}>
          <h3 className="font-heading text-lg font-medium" style={{ color: "#1C211F" }}>
            {isEditing ? "Edit Pop-up" : "Add Pop-up"}
          </h3>
          <button
            onClick={() => setDrawerOpen(false)}
            className="p-1.5 rounded-lg transition-colors"
            style={{ color: "#4A5450" }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(26,92,55,0.08)")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
          >
            <X size={20} weight="bold" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={(e) => e.preventDefault()} className="flex-1 overflow-y-auto px-6 py-6 space-y-5">

          {/* Title */}
          <div>
            <label className={labelCls} style={{ color: "#4A5450" }}>Title</label>
            <input
              required
              type="text"
              placeholder="e.g. New Research Published"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              className={inputCls}
              style={inputStyle}
              onFocus={(e) => (e.target.style.borderColor = "#1A5C37")}
              onBlur={(e) => (e.target.style.borderColor = "rgba(26,92,55,0.2)")}
              data-testid="popup-title-input"
            />
          </div>

          {/* Description */}
          <div>
            <label className={labelCls} style={{ color: "#4A5450" }}>Description</label>
            <textarea
              required
              rows={4}
              placeholder="Brief message to show on the pop-up"
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              className={inputCls}
              style={{ ...inputStyle, resize: "vertical" }}
              onFocus={(e) => (e.target.style.borderColor = "#1A5C37")}
              onBlur={(e) => (e.target.style.borderColor = "rgba(26,92,55,0.2)")}
              data-testid="popup-description-input"
            />
          </div>

          {/* Button Text */}
          <div>
            <label className={labelCls} style={{ color: "#4A5450" }}>
              Button Text <span className="opacity-50 normal-case tracking-normal">(optional)</span>
            </label>
            <input
              type="text"
              placeholder="Learn More"
              value={form.button_text}
              onChange={(e) => setForm((f) => ({ ...f, button_text: e.target.value }))}
              className={inputCls}
              style={inputStyle}
              onFocus={(e) => (e.target.style.borderColor = "#1A5C37")}
              onBlur={(e) => (e.target.style.borderColor = "rgba(26,92,55,0.2)")}
              data-testid="popup-button-text-input"
            />
            <p className="mt-1.5 text-xs" style={{ color: "#7A8A84" }}>
              Label shown on the action button. Defaults to &quot;Learn More&quot; if left blank.
            </p>
          </div>

          {/* Pop-up Type */}
          <div>
            <label className={labelCls} style={{ color: "#4A5450" }}>Pop-up Type</label>
            <div className="space-y-2">
              {TYPE_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  className="flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors"
                  style={{
                    borderColor: form.popup_type === opt.value ? "#1A5C37" : "rgba(26,92,55,0.15)",
                    backgroundColor: form.popup_type === opt.value ? "rgba(26,92,55,0.04)" : "transparent",
                  }}
                >
                  <input
                    type="radio"
                    name="popup_type"
                    value={opt.value}
                    checked={form.popup_type === opt.value}
                    onChange={() => setForm((f) => ({ ...f, popup_type: opt.value, report_id: "", link_url: "" }))}
                    className="mt-0.5 flex-shrink-0"
                    style={{ accentColor: "#1A5C37" }}
                  />
                  <div>
                    <p className="text-sm font-medium" style={{ color: "#1C211F" }}>{opt.label}</p>
                    <p className="text-xs" style={{ color: "#4A5450" }}>{opt.desc}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Conditional: Report selector */}
          {form.popup_type === "report" && (
            <div>
              <label className={labelCls} style={{ color: "#4A5450" }}>
                Select Report <span className="opacity-50 normal-case tracking-normal">(optional)</span>
              </label>
              <select
                value={form.report_id}
                onChange={(e) => setForm((f) => ({ ...f, report_id: e.target.value }))}
                className={inputCls}
                style={inputStyle}
                onFocus={(e) => (e.target.style.borderColor = "#1A5C37")}
                onBlur={(e) => (e.target.style.borderColor = "rgba(26,92,55,0.2)")}
                data-testid="popup-report-select"
              >
                <option value="">No report (title &amp; message only)</option>
                {reports.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.title} ({r.year})
                  </option>
                ))}
              </select>
              {selectedReport && (
                <div
                  className="flex items-center gap-3 px-4 py-3 rounded-lg border mt-2"
                  style={{ backgroundColor: "rgba(26,92,55,0.04)", borderColor: "rgba(26,92,55,0.15)" }}
                >
                  {selectedReport.file_type === "pdf"
                    ? <FilePdf size={20} weight="duotone" style={{ color: "#1A5C37" }} />
                    : <Image size={20} weight="duotone" style={{ color: "#1A5C37" }} />}
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate" style={{ color: "#1C211F" }}>{selectedReport.title}</p>
                    <p className="text-xs" style={{ color: "#4A5450" }}>{selectedReport.category} &middot; {selectedReport.year}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Conditional: URL input for internal/external */}
          {(form.popup_type === "internal_link" || form.popup_type === "external_link") && (
            <div>
              <label className={labelCls} style={{ color: "#4A5450" }}>
                {form.popup_type === "internal_link" ? "Internal URL" : "External URL"}
              </label>
              <input
                required
                type="text"
                placeholder={form.popup_type === "internal_link" ? "e.g. /petition" : "e.g. https://example.com"}
                value={form.link_url}
                onChange={(e) => setForm((f) => ({ ...f, link_url: e.target.value }))}
                className={inputCls}
                style={inputStyle}
                onFocus={(e) => (e.target.style.borderColor = "#1A5C37")}
                onBlur={(e) => (e.target.style.borderColor = "rgba(26,92,55,0.2)")}
                data-testid="popup-link-url-input"
              />
              <p className="mt-1.5 text-xs" style={{ color: "#7A8A84" }}>
                {form.popup_type === "internal_link"
                  ? "Opens in the same tab. Use a relative path like /petition or /gallery."
                  : "Opens in a new tab. Include https:// for external links."}
              </p>
            </div>
          )}

          {error && (
            <p className="text-xs px-3 py-2 rounded-lg" style={{ color: "#B85C38", backgroundColor: "rgba(184,92,56,0.08)" }}>
              {error}
            </p>
          )}
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t flex gap-3 flex-shrink-0" style={{ borderColor: "rgba(26,92,55,0.1)" }}>
          <button
            type="button"
            onClick={() => setDrawerOpen(false)}
            className="flex-1 py-2.5 rounded-lg text-sm font-medium border transition-colors"
            style={{ borderColor: "rgba(26,92,55,0.2)", color: "#4A5450" }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="flex-1 py-2.5 rounded-lg text-sm font-medium transition-colors"
            style={{ backgroundColor: submitting ? "#4a8c62" : "#1A5C37", color: "#F7F5F0", cursor: submitting ? "not-allowed" : "pointer" }}
            data-testid="submit-popup-btn"
          >
            {submitting ? "Saving..." : isEditing ? "Save Changes" : "Save Pop-up"}
          </button>
        </div>
      </div>
    </div>
  );
}
